import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip the /me check; AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const dancerLogin = async (username, pin) => {
    const res = await api.post("/auth/dancer-login", { username, pin });
    if (res.data.session_token) {
      localStorage.setItem("session_token", res.data.session_token);
    }
    setUser(res.data.user);
    return res.data.user;
  };

  const emailLogin = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (res.data.session_token) localStorage.setItem("session_token", res.data.session_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const emailRegister = async (payload) => {
    const res = await api.post("/auth/register", payload);
    if (res.data.session_token) localStorage.setItem("session_token", res.data.session_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const forgotPassword = async (email) => {
    const res = await api.post("/auth/forgot-password", { email });
    return res.data;
  };

  const resetPassword = async (token, password) => {
    const res = await api.post("/auth/reset-password", { token, password });
    return res.data;
  };

  const studioSetup = async ({ name, email, password }) => {
    const res = await api.post("/auth/setup", { name, email, password });
    if (res.data.session_token) localStorage.setItem("session_token", res.data.session_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const finishSession = (u, token) => {
    if (token) localStorage.setItem("session_token", token);
    setUser(u);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("session_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, dancerLogin, emailLogin, emailRegister, forgotPassword, resetPassword, studioSetup, finishSession, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}
